import React, { useEffect, useRef, useState } from 'react';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { chat } from '../translate';
import Api from '../Api';

// ✅ ADD THIS IMPORT
import { getSocket } from '../socketClient';
import notifySound from '../assets/notify.wav';

function Chat({ controlledOpen, onRequestClose, onRequestOpen }) {
    const [open, setOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const effectiveOpen = isControlled ? controlledOpen : open;
    const [mode, setMode] = useState('call'); // 'call' | 'help'
    const [callForm, setCallForm] = useState({ name: '', phone: '', time: '', type: "call" });
    const [helpForm, setHelpForm] = useState({ message: '', type: "message" });
    const [status, setStatus] = useState('');
    const [messages, setMessages] = useState([]);
    const [callSubmitted, setCallSubmitted] = useState(false);
    const threadRef = useRef(null);
    const audioRef = useRef(null);
    const getLang = () => +localStorage.getItem('atsLang') || 1;
    const dayWrapperStyle = { display: 'flex', justifyContent: 'center', margin: '8px 0' };
    const dayLabelStyle = { padding: '4px 12px', borderRadius: '16px', background: '#e6f0ff', color: '#4a5568', fontSize: 12 };

    const toggle = () => {
        if (isControlled) onRequestClose?.();
        else setOpen(prev => !prev);
    };
    const loadHistory = async () => {
        try {
            const { data } = await Api.getHelpHistory(); // you add it in Api
            if (data?.ok) setMessages(data.messages || []);
        } catch (e) {
            console.log("history error", e);
        }
    };
    useEffect(() => {
        const token =
            localStorage.getItem("access_token") ||
            localStorage.getItem("token");

        if (!token) {
            console.log("[SOCKET] no token -> not connecting");
            return;
        }

        const socket = getSocket();
        if (!socket) return;

        const onReply = (payload) => {
            console.log("[SOCKET] help:reply", payload);

            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now() + Math.random(),
                    from: "admin",
                    text: payload?.message || "",
                    meta: payload,
                    createdAt: payload?.createdAt || new Date().toISOString(),
                },
            ]);

            setMode("help");
            if (onRequestOpen) onRequestOpen();
            else setOpen(true);
            playNotify();
        };

        // optional debug
        const onConnect = () => console.log("[SOCKET] connected:", socket.id);
        const onDisconnect = (r) => console.log("[SOCKET] disconnected:", r);
        const onErr = (e) => console.log("[SOCKET] connect_error:", e?.message || e);

        socket.on("help:reply", onReply);
        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("connect_error", onErr);

        return () => {
            socket.off("help:reply", onReply);
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
            socket.off("connect_error", onErr);
        };
    }, []);

    const callHours = ['09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20'];
    const callMinutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
    const isTimeInRange = (value) => {
        if (!value || !value.includes(':')) return false;
        const [h, m] = value.split(':');
        return callHours.includes(h) && callMinutes.includes(m);
    };

    const handleCallSubmit = async (e) => {
        e.preventDefault();
        if (!callForm.name.trim() || !callForm.phone.trim()) {
            setStatus(chat.callRequired[getLang()] || 'Name and phone are required to register a call.');
            return;
        }
        if (!isTimeInRange(callForm.time)) {
            setStatus(chat.timeRange[getLang()] || 'Please pick a time between 09:00 and 20:00.');
            return;
        }
        try {
            const { data } = await Api.createHelp(callForm);
            console.log('Call registration request', data);
        } catch (e) {
            console.log('Error', e);
            setStatus(chat.error?.[getLang()] || 'Error, try again.');
            return;
        }
        console.log('Call registration request', callForm);
        setStatus(chat.callReserved[getLang()] || 'The call is reserved. We will answer to it during an hour. If it is an emergency, please call +37444720101.');
        setCallSubmitted(true);
    };

    const handleHelpSubmit = async (e) => {
        e.preventDefault();
        if (!helpForm.message.trim()) {
            setStatus(chat.helpRequired[getLang()] || 'Please enter a short message for support.');
            return;
        }
        const nowIso = new Date().toISOString();
        const newMessage = { id: Date.now(), from: 'user', text: helpForm.message, createdAt: nowIso };
        setMessages(prev => [...prev, newMessage]);
        setStatus('');
        try {
            const { data } = await Api.createHelp(helpForm);
            console.log('Help message', data);
            setMessages(prev => [
                ...prev,
                {
                    id: Date.now() + 1,
                    from: 'system',
                    text: chat.sent[localStorage.getItem('atsLang') || 1] || 'Message sent.',
                    createdAt: nowIso,
                }
            ]);
        } catch (e) {
            console.log('Error', e);
            setMessages(prev => [
                ...prev,
                {
                    id: Date.now() + 2,
                    from: 'system',
                    text: chat.error[localStorage.getItem('atsLang') || 1] || 'Error sending message.',
                    createdAt: nowIso,
                }
            ]);
        }
        console.log('Help message', helpForm);
        setStatus(chat.sent[localStorage.getItem('atsLang') || 1] || 'Message sent. Support will reply soon.');
        setHelpForm({ ...helpForm, message: '' });
    };

    useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [messages, mode]);

    const handleHelpKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleHelpSubmit(e);
        }
    };

    const formatTimestamp = (value) => {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    };
    const getDayKey = (value) => {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
    };
    const formatDayLabel = (value) => {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString();
    };

    useEffect(() => {
        audioRef.current = new Audio(notifySound);
    }, []);

    const playNotify = () => {
        if (!audioRef.current) return;
        try {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => { });
        } catch (e) {
            console.log('notify sound error', e);
        }
    };

    return (
        <div className="chat-widget">
            {effectiveOpen ? (
                <div className="chat-card" style={{ height: mode === 'help' ? '550px' : '450px' }}>
                    <div className="chat-card__top">
                        <div>
                            <div className="chat-card__title">{chat.title[localStorage.getItem('atsLang') || 1]}</div>
                            <div className="chat-card__subtitle">{chat.subtitle[localStorage.getItem('atsLang') || 1]}</div>
                        </div>
                        <button
                            aria-label="Close chat"
                            onClick={toggle}
                            className="chat-card__close"
                        >
                            ×
                        </button>
                    </div>

                    <div className="chat-tabs">
                        <button
                            type="button"
                            className={`chat-tab ${mode === 'call' ? 'is-active' : ''}`}
                            onClick={() => { setMode('call'); setCallSubmitted(false); }}
                        >
                            {chat.call[localStorage.getItem('atsLang') || 1]}
                        </button>
                        <button
                            type="button"
                            className={`chat-tab ${mode === 'help' ? 'is-active' : ''}`}
                            onClick={() => {
                                setMode('help');
                                setStatus('');
                                loadHistory();
                            }}
                        >
                            {chat.help[localStorage.getItem('atsLang') || 1]}
                        </button>
                    </div>

                    {mode === 'call' ? (
                        callSubmitted ? (
                            <div className="chat-form" style={{ alignItems: 'flex-start' }}>
                                <div className="chat-status">
                                    <strong>{status}</strong>
                                </div>
                                <button
                                    type="button"
                                    className="chat-submit"
                                    onClick={() => { setCallSubmitted(false); setStatus(''); setCallForm({ name: '', phone: '', time: '', type: "call" }); }}
                                >
                                    {chat.back?.[localStorage.getItem('atsLang') || 1] || 'Back'}
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleCallSubmit} className="chat-form">
                                <input
                                    type="text"
                                    placeholder={chat.name[localStorage.getItem('atsLang') || 1]}
                                    value={callForm.name}
                                    onChange={(e) => setCallForm({ ...callForm, name: e.target.value })}
                                    className="chat-input"
                                />
                                <input
                                    type="tel"
                                    placeholder={chat.phone[localStorage.getItem('atsLang') || 1]}
                                    value={callForm.phone}
                                    onChange={(e) => setCallForm({ ...callForm, phone: e.target.value })}
                                    className="chat-input"
                                />
                                <div className="chat-time-group">
                                    <div >
                                        <span className="chat-time-label">
                                            {chat.time[localStorage.getItem('atsLang') || 1]}
                                        </span>
                                        <div className="chat-time-row">
                                            <div className="chat-time-select">

                                                <select
                                                    value={(callForm.time || '').split(':')[0] || ''}
                                                    onChange={(e) => {
                                                        const hour = e.target.value;
                                                        const minute = (callForm.time || '').split(':')[1] || '';
                                                        const timeVal = hour ? (minute ? `${hour}:${minute}` : `${hour}:`) : (minute ? `:${minute}` : '');
                                                        setCallForm({ ...callForm, time: timeVal });
                                                    }}
                                                    className="chat-input chat-time-input"
                                                >
                                                    <option value="">--</option>
                                                    {callHours.map((h) => (
                                                        <option key={h} value={h}>{h}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="chat-time-select">
                                               
                                                <select
                                                    value={(callForm.time || '').split(':')[1] || ''}
                                                    onChange={(e) => {
                                                        const minute = e.target.value;
                                                        const hour = (callForm.time || '').split(':')[0] || '';
                                                        const timeVal = hour ? `${hour}:${minute}` : (minute ? `:${minute}` : '');
                                                        setCallForm({ ...callForm, time: timeVal });
                                                    }}
                                                    className="chat-input chat-time-input"
                                                    disabled={!((callForm.time || '').split(':')[0])}
                                                >
                                                    <option value="">--</option>
                                                    {callMinutes.map((m) => (
                                                        <option key={m} value={m}>{m}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    className="chat-submit"
                                >
                                    {chat.send[localStorage.getItem('atsLang') || 1]}
                                </button>
                            </form>
                        )
                    ) : (
                        <form onSubmit={handleHelpSubmit} className="chat-form chat-form--help">
                            <div className="chat-thread" ref={threadRef}>
                                {messages.length === 0 ? (
                                    <div className="chat-thread__empty">
                                        {chat.start[localStorage.getItem('atsLang') || 1] || 'Start a conversation'}
                                    </div>
                                ) : (
                                    (() => {
                                        let lastDay = '';
                                        return messages.map(msg => {
                                            const dayKey = getDayKey(msg.createdAt);
                                            const showDay = dayKey && dayKey !== lastDay;
                                            if (dayKey) {
                                                lastDay = dayKey;
                                            }
                                            return (
                                                <React.Fragment key={msg.id}>
                                                    {showDay ? (
                                                        <div className="chat-thread__day" style={dayWrapperStyle}>
                                                            <span style={dayLabelStyle}>{formatDayLabel(msg.createdAt)}</span>
                                                        </div>
                                                    ) : null}
                                                    <div className={`chat-bubble chat-bubble--${msg.from}`}>
                                                        <div className="chat-bubble__meta">
                                                            <span className="chat-bubble__label">
                                                                {msg.from === 'admin' ? 'Operator' : ""}
                                                            </span>
                                                            <span className="chat-bubble__time">
                                                                {formatTimestamp(msg.createdAt)}
                                                            </span>
                                                        </div>
                                                        <div className="chat-bubble__text">
                                                            {msg.text}
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            )
                                        })
                                    })()
                                )}
                            </div>

                            <textarea
                                placeholder={chat.message[localStorage.getItem('atsLang') || 1]}
                                value={helpForm.message}
                                onChange={(e) => setHelpForm({ ...helpForm, message: e.target.value })}
                                rows={2}
                                className="chat-textarea"
                                onKeyDown={handleHelpKeyDown}
                            />
                            <button type="submit" className="chat-submit">
                                {chat.send[localStorage.getItem('atsLang') || 1]}
                            </button>
                        </form>
                    )}

                    {status && !(mode === 'call' && callSubmitted) ? (
                        <div className="chat-status">
                            <strong>{status}</strong>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {!effectiveOpen && !isControlled && (
                <button onClick={() => setOpen(true)} aria-label="Open chat" className="chat-toggle">
                    <ChatBubbleIcon className="chat-toggle__icon" />
                </button>
            )}
        </div>
    );
}

export default Chat;
